import type { LiveProvider } from "@refinedev/core";
import { directus } from "./directus";
import { eventsUrl, liveEventType, parseLiveBlock, type LiveChange } from "../lib/liveEvents";

type Listener = (change: LiveChange) => void;
const listeners = new Set<Listener>();
let sharedController: AbortController | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let idleCloseTimer: ReturnType<typeof setTimeout> | null = null;

function ensureSharedStream() {
  if (idleCloseTimer) {
    clearTimeout(idleCloseTimer);
    idleCloseTimer = null;
  }
  if (sharedController || listeners.size === 0) return;
  const controller = new AbortController();
  sharedController = controller;

  (async () => {
    try {
      const token = await directus.getToken();
      if (!token || listeners.size === 0) {
        sharedController = null;
        return;
      }
      const response = await fetch(eventsUrl(String(directus.url)), {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        sharedController = null;
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!controller.signal.aborted) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let index: number;
        while ((index = buffer.indexOf("\n\n")) >= 0) {
          const block = buffer.slice(0, index);
          buffer = buffer.slice(index + 2);
          const change = parseLiveBlock(block);
          if (!change) continue;
          for (const listener of [...listeners]) {
            try { listener(change); } catch { /* ignore */ }
          }
        }
      }
    } catch {
      // Best-effort SSE stream; reconnect if subscribers remain.
    } finally {
      if (sharedController === controller) {
        sharedController = null;
      }
      if (listeners.size > 0 && !reconnectTimer) {
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          ensureSharedStream();
        }, 3_000);
      }
    }
  })();
}

function scheduleIdleClose() {
  if (listeners.size > 0 || idleCloseTimer) return;
  idleCloseTimer = setTimeout(() => {
    idleCloseTimer = null;
    if (listeners.size > 0) return;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (sharedController) {
      sharedController.abort();
      sharedController = null;
    }
  }, 5_000);
}

// Single multiplexed SSE stream -> Refine live events.
export const liveProvider: LiveProvider = {
  subscribe: ({ channel, params, callback }: any) => {
    const resource = params?.resource ?? String(channel).replace(/^resources\//, "");
    const listener: Listener = (change) => {
      if (resource && change.collection !== resource) return;
      callback({
        channel,
        type: liveEventType(change.action),
        date: new Date(),
        payload: { ids: [change.key] },
      });
    };
    listeners.add(listener);
    ensureSharedStream();

    return {
      unsubscribe: () => {
        listeners.delete(listener);
        scheduleIdleClose();
      },
    };
  },
  unsubscribe: (sub: any) => {
    sub?.unsubscribe?.();
  },
};
